import { useCallback } from 'react'
import { IRowNode } from 'ag-grid-community'
import { AccountInfo } from '../AccountFilter'

/**
 * Hook to implement ag-Grid's external filter system
 * for account filtering based on selectedAccountIds or diff threshold (OR logic)
 */
export const useExternalAccountFilter = (
  selectedAccountIds: Set<number>,
  allAccountIds: Set<number>,
  filterActive: boolean,
  accounts: AccountInfo[] = [],
  enableDiffFilter: boolean = false,
  minDiff: number = 500,
  enableCommentFilter: boolean = false
) => {
  // Check if filter is active
  // Filter is active if filterActive is true AND (some accounts are selected OR diff-filter is active)
  const isExternalFilterPresent = useCallback((): boolean => {
    if (!filterActive) {
      return false
    }

    const hasSelection = selectedAccountIds.size > 0
    const hasDiffFilter = enableDiffFilter
    const hasCommentFilter = enableCommentFilter

    // If no filters are active, no external filter should run
    if (!hasSelection && !hasDiffFilter && !hasCommentFilter) {
      return false
    }

    return true
  }, [selectedAccountIds, filterActive, enableDiffFilter, enableCommentFilter])

  // Check if a row should be shown based on filter
  const doesExternalFilterPass = useCallback(
    (node: IRowNode): boolean => {
      // If filter is not active, show all rows
      if (!isExternalFilterPresent()) {
        return true
      }

      const rowData = node.data
      if (!rowData) {
        return true
      }

      const accountId = rowData.accountId as number | undefined

      // Rows without accountId are group rows (headers/summarized rows)
      // Always show these so that ag-Grid can handle the hierarchy
      if (!accountId) {
        return true
      }

      // For normal rows with accountId: check both filter types with OR logic

      // 1. Check account filter (if any accounts are selected)
      const hasSelection = selectedAccountIds.size > 0
      const passesAccountFilter = hasSelection
        ? selectedAccountIds.has(accountId)
        : false

      // 2. Check diff-filter (if active)
      let passesDiffFilter = false
      if (enableDiffFilter) {
        const account = accounts.find((acc) => acc.accountId === accountId)
        if (
          account &&
          account.difference !== null &&
          account.difference !== undefined
        ) {
          // Check if absolute value of diff is greater than threshold
          passesDiffFilter = Math.abs(account.difference) > Math.abs(minDiff)
        }
      }

      // 3. Check comment-filter (if active)
      const hasComment =
        typeof rowData.comment === 'string' && rowData.comment.trim().length > 0
      const passesCommentFilter = enableCommentFilter ? hasComment : false

      // OR logic: Show row if it passes ANY of the filters
      const activeFilterResults: boolean[] = []

      if (hasSelection) {
        activeFilterResults.push(passesAccountFilter)
      }

      if (enableDiffFilter) {
        activeFilterResults.push(passesDiffFilter)
      }

      if (enableCommentFilter) {
        activeFilterResults.push(passesCommentFilter)
      }

      if (activeFilterResults.length > 0) {
        return activeFilterResults.some(Boolean)
      }

      return true
    },
    [
      selectedAccountIds,
      isExternalFilterPresent,
      accounts,
      enableDiffFilter,
      minDiff,
      enableCommentFilter,
    ]
  )

  return {
    isExternalFilterPresent,
    doesExternalFilterPass,
  }
}
